import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';

import IconPlus from '../IconPlus/IconPlus';

import filterPlainCss from '../FilterPlain/FilterPlain.module.css';
import selectMultipleCss from '../SelectMultipleFilter/SelectMultipleFilter.module.css';
import checkboxCss from '../../../components/FieldCheckbox/FieldCheckbox.module.css';

const IconCheckbox = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="none">
    <rect className={checkboxCss.box} x="0.5" y="0.5" width="13" height="13" rx="1" strokeWidth="1" />
    <polyline className={checkboxCss.checked} points="2.5 7 5.5 10 11.5 4" strokeWidth="1" stroke="currentColor" />
  </svg>
);

/**
 * Merges two multi-enum checkbox filters (Category and Collection) into one
 * alphabetically-sorted list under the Category header.
 */
const CombinedCategoryCollectionFilter = props => {
  const {
    id,
    label,
    categoryQueryParamName,
    categorySearchMode,
    collectionQueryParamName,
    collectionSearchMode,
    initialValues,
    onSubmit,
    categoryOptions,
    collectionOptions,
    onCrossCollectionSelect,
    className,
    rootClassName,
  } = props;

  const [isOpen, setIsOpen] = useState(true);

  const parseSelected = (paramValue) =>
    paramValue ? parseSelectFilterOptions(paramValue) : [];

  const selectedCategories = parseSelected(initialValues?.[categoryQueryParamName]);
  const selectedCollections = parseSelected(initialValues?.[collectionQueryParamName]);

  const catItems = (categoryOptions || []).map(opt => ({
    group: 'category',
    option: opt.option,
    label: opt.label,
  }));
  const colItems = (collectionOptions || []).map(opt => ({
    group: 'collection',
    option: opt.option,
    label: opt.label,
  }));

  const allOptions = [...catItems, ...colItems].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const formatValue = (selections, searchMode) => {
    if (!selections.length) return null;
    const mode = searchMode ? `${searchMode}:` : '';
    return `${mode}${selections.join(',')}`;
  };

  const handleCategoryToggle = option => {
    const isChecked = selectedCategories.includes(option);
    const next = isChecked
      ? selectedCategories.filter(o => o !== option)
      : [...selectedCategories, option];
    onSubmit({ [categoryQueryParamName]: formatValue(next, categorySearchMode) });
  };

  const handleCollectionToggle = option => {
    const isChecked = selectedCollections.includes(option);
    const next = isChecked
      ? selectedCollections.filter(o => o !== option)
      : [...selectedCollections, option];
    onSubmit({ [collectionQueryParamName]: formatValue(next, collectionSearchMode) });
  };

  const handleClear = () => {
    onSubmit({
      [categoryQueryParamName]: null,
      [collectionQueryParamName]: null,
    });
  };

  const hasSelection = selectedCategories.length > 0 || selectedCollections.length > 0;
  const classes = classNames(rootClassName || filterPlainCss.root, className);

  return (
    <div className={classes}>
      <div className={filterPlainCss.filterHeader}>
        <button className={filterPlainCss.labelButton} onClick={() => setIsOpen(o => !o)}>
          <span className={filterPlainCss.labelButtonContent}>
            <span className={filterPlainCss.labelWrapper}>
              <span className={filterPlainCss.label}>{label}</span>
            </span>
            <span className={filterPlainCss.openSign}>
              <IconPlus isOpen={isOpen} isSelected={hasSelection} />
            </span>
          </span>
        </button>
      </div>

      <div className={classNames(filterPlainCss.plain, { [filterPlainCss.isOpen]: isOpen })}>
        <fieldset className={selectMultipleCss.fieldGroupPlain}>
          <ul className={selectMultipleCss.list}>
            {allOptions.map(item => {
              const isChecked = item.group === 'category'
                ? selectedCategories.includes(item.option)
                : selectedCollections.includes(item.option);
              const checkboxId = `${id}-${item.group}-${item.option}`;
              const handleChange = item.group === 'category'
                ? () => handleCategoryToggle(item.option)
                : () => onCrossCollectionSelect
                  ? onCrossCollectionSelect(item.option)
                  : handleCollectionToggle(item.option);

              return (
                <li key={checkboxId} className={selectMultipleCss.item}>
                  <span className={checkboxCss.root}>
                    <input
                      id={checkboxId}
                      type="checkbox"
                      className={checkboxCss.input}
                      checked={isChecked}
                      onChange={handleChange}
                    />
                    <label htmlFor={checkboxId} className={checkboxCss.label}>
                      <span className={checkboxCss.checkboxWrapper}>
                        <IconCheckbox />
                      </span>
                      <span className={classNames(checkboxCss.text, checkboxCss.textRoot)}>
                        {item.label}
                      </span>
                    </label>
                  </span>
                </li>
              );
            })}
          </ul>
        </fieldset>

        <button className={filterPlainCss.clearButton} onClick={handleClear}>
          <FormattedMessage id="FilterPlain.clear" />
        </button>
      </div>
    </div>
  );
};

export default CombinedCategoryCollectionFilter;
